/**
 * @Module: Kitchen Firestore Service
 * @Status: Ready
 * @Summary: Single-document CRUD for the user's kitchen profile in Firestore
 * @Description: Kitchen profile is stored at /users/{uid}/profile/kitchen.
 *   Uses a narrowed skillLevel union type. save() overwrites the full doc;
 *   get() returns null when no profile exists yet (no error).
 * @NextSteps: Add versioning, partial update for individual sections
 */

import {
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  type Firestore,
} from 'firebase/firestore';
import type { KitchenProfile } from '../../types/kitchen';
import { kitchenFromFirestore } from './converters';
import { AppError, wrapFirestoreError } from './errors';

/** Narrowed skill level — stricter than the loose `string` in KitchenProfile. */
export type SkillLevel = 'beginner' | 'intermediate' | 'advanced' | 'pro';

const VALID_SKILL_LEVELS: readonly string[] = [
  'beginner',
  'intermediate',
  'advanced',
  'pro',
];

function assertUid(uid: string | null | undefined): asserts uid is string {
  if (!uid) throw new AppError('AUTH_REQUIRED', 'Sign in to manage kitchen profile');
}

function profileDoc(db: Firestore, uid: string) {
  return doc(db, 'users', uid, 'profile', 'kitchen');
}

function validateSkillLevel(level: string): void {
  if (!VALID_SKILL_LEVELS.includes(level)) {
    throw new AppError(
      'INVALID_INPUT',
      `Invalid skill level: "${level}". Must be one of: ${VALID_SKILL_LEVELS.join(', ')}`,
      'kitchen.save',
    );
  }
}

export function createKitchenService(db: Firestore) {
  return {
    /** Save (overwrite) the kitchen profile. Validates skillLevel. */
    async save(uid: string | null, profile: KitchenProfile): Promise<void> {
      assertUid(uid);
      validateSkillLevel(profile.skillLevel);

      try {
        await setDoc(profileDoc(db, uid), {
          ...profile,
          updatedAt: serverTimestamp(),
          schemaVersion: 2,
        });
      } catch (error) {
        wrapFirestoreError(error, 'kitchen.save');
      }
    },

    /** Delete the kitchen profile from Firestore. */
    async remove(uid: string | null): Promise<void> {
      assertUid(uid);
      try {
        await deleteDoc(profileDoc(db, uid));
      } catch (error) {
        wrapFirestoreError(error, 'kitchen.remove');
      }
    },

    /** Fetch the kitchen profile. Returns null if none exists. */
    async get(uid: string | null): Promise<KitchenProfile | null> {
      assertUid(uid);

      try {
        const snap = await getDoc(profileDoc(db, uid));
        if (!snap.exists()) return null;
        return kitchenFromFirestore(snap.data() as Record<string, unknown>);
      } catch (error) {
        wrapFirestoreError(error, 'kitchen.get');
      }
    },
  };
}
